import { useState } from 'react'
import i18n from 'i18n'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { CERTIFICATE_INITIAL_STATE } from 'constants/industry.constants'

import { FileUpload } from 'components/FileUpload'
import { Button, Icon, Input, InputDate, InputSelect, Modal } from 'components'

import * as S from './styled'
import { YupValidator } from 'services/yupValidator.service'
import { certificationSchema } from 'store/Industry/Industry.validator'
import { IconButton } from '@mui/material'

const alert = () => [
  { label: i18n.t('indCertifications:alert30'), value: '30days' },
  { label: i18n.t('indCertifications:alert60'), value: '60days' },
  { label: i18n.t('indCertifications:alert90'), value: '90days' }
]

export type ModalAddCertificationsProps = {
  onExit: () => void
  certification: typeof CERTIFICATE_INITIAL_STATE
  index: number
  onHandleCertification: (_certificate, _index) => void
}

export function ModalAddCertifications({
  onExit,
  certification,
  index,
  onHandleCertification
}: ModalAddCertificationsProps) {
  const { t } = useTranslation()

  const adjustDateStringToUTC = dateString => {
    if (!dateString) return null
    const [year, month, day] = dateString.split('-')
    const date = new Date(
      Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day))
    )
    date.setDate(date.getDate() + 1)
    return date
  }

  const [errors, setErrors] = useState({ ...CERTIFICATE_INITIAL_STATE })
  const [certificationData, setCertificationData] = useState({
    ...certification,
    dtEmitted: adjustDateStringToUTC(certification.dtEmitted),
    dtExpiry: adjustDateStringToUTC(certification.dtExpiry)
  })

  function err(name: string) {
    if (errors) {
      return errors[name]
    } else {
      return undefined
    }
  }

  async function onSetCertification() {
    const [isValid, validationError] = await new YupValidator(
      certificationSchema()
    ).validate(certificationData)

    if (!isValid) {
      return setErrors(validationError as typeof CERTIFICATE_INITIAL_STATE)
    }

    const today = new Date()
    const isCertificationValid = (date: string) => {
      if (!date) return null
      const [year, month, day] = date.split('-')
      const formattedDate = new Date(
        Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day))
      )
      formattedDate.setDate(formattedDate.getDate() + 1)
      const isValid = today <= formattedDate

      return isValid
    }

    const certificationDataFormat = {
      ...certificationData,
      dtEmitted: format(new Date(certificationData.dtEmitted), 'yyyy-MM-dd'),
      dtExpiry: format(new Date(certificationData.dtExpiry), 'yyyy-MM-dd'),
      status: isCertificationValid(
        format(new Date(certificationData.dtExpiry), 'yyyy-MM-dd')
      )
    }

    onHandleCertification(certificationDataFormat, index)
    onExit()
  }

  const button = (
    <Button variant="primary" onClick={onSetCertification} size="medium">
      <p>{t('common:save')}</p>
    </Button>
  )

  const handleInput = (value, field) => {
    setCertificationData(prevState => ({
      ...prevState,
      [field]: value
    }))
  }

  const handleLoadImage = (acceptedFiles: File[], field: string) => {
    const fileToLoad = acceptedFiles[0]
    const reader = new FileReader()

    reader.onloadend = () => {
      const base64String = reader.result as string

      const newFile = {
        src: base64String,
        filename: fileToLoad.name
      }

      setCertificationData(prevData => ({
        ...prevData,
        [field]: newFile
      }))
    }

    reader.readAsDataURL(fileToLoad)
  }

  const handleRemoveImage = (field: string) => {
    setCertificationData(prevData => ({
      ...prevData,
      [field]: { src: '', filename: '' }
    }))
  }

  const handleNewEmailChange = (event, index) => {
    const { value } = event.target

    const emailsArray = certificationData.emails.split(',')
    emailsArray[index] = value

    const newEmailsString = emailsArray.join(',')

    setCertificationData(prevState => ({
      ...prevState,
      emails: newEmailsString
    }))
  }

  const handleEmailBlur = () => {
    const emailsArray = certificationData.emails.split(',')
    const filteredEmailsArray = emailsArray.filter(email => email.trim() !== '')
    const newEmailsString = filteredEmailsArray.join(',')

    setCertificationData(prevState => ({
      ...prevState,
      emails: newEmailsString
    }))
  }

  const handleAddEmail = () => {
    const emailsArray = certificationData.emails
      ? certificationData.emails.split(',')
      : []
    emailsArray.push('')
    const newEmailsString = emailsArray.join(',')

    setCertificationData(prevState => ({
      ...prevState,
      emails: newEmailsString
    }))
  }

  const handleRemoveEmail = () => {
    const emailsArray = certificationData.emails.split(',')
    if (emailsArray.length > 1) {
      emailsArray.pop()
      const newEmailsString = emailsArray.join(',')
      setCertificationData(prevState => ({
        ...prevState,
        emails: newEmailsString
      }))
    }
  }

  const content = (
    <S.Wrapper container>
      <S.ModalTitle>{t('indCertifications:titleModal')}</S.ModalTitle>
      <S.FullGrid item xs={12}>
        <S.GridHeader>
          <S.GridTitle item container xs={12}>
            <S.BoxTitle>{t('indCertifications:basicInfo')}</S.BoxTitle>
          </S.GridTitle>
        </S.GridHeader>
        <S.GridFieldsPhoto container>
          <S.GridFields container item sm={12}>
            <S.GridInput item sm={12} md={6}>
              <Input
                fullWidth
                label={t('indCertifications:certificationName')}
                value={certificationData.name}
                error={Boolean(err('name'))}
                helperText={err('name')}
                onInput={value => handleInput(value, 'name')}
              />
            </S.GridInput>
            <S.GridInput item sm={12} md={6}>
              <Input
                fullWidth
                label={t('indCertifications:acronymous')}
                value={certificationData.acronym}
                error={Boolean(err('acronym'))}
                helperText={err('acronym')}
                onInput={value => handleInput(value, 'acronym')}
              />
            </S.GridInput>
            <S.GridInput item sm={12} md={6}>
              <Input
                fullWidth
                label={t('indCertifications:nameCertifier')}
                value={certificationData.nameCertifier}
                error={Boolean(err('nameCertifier'))}
                helperText={err('nameCertifier')}
                onInput={value => handleInput(value, 'nameCertifier')}
              />
            </S.GridInput>
            <S.GridInput item sm={12} md={6}>
              <Input
                fullWidth
                label={t('indCertifications:idCertifier')}
                value={certificationData.idCertifier}
                error={Boolean(err('idCertifier'))}
                helperText={err('idCertifier')}
                onInput={value => handleInput(value, 'idCertifier')}
              />
            </S.GridInput>
          </S.GridFields>
        </S.GridFieldsPhoto>

        <S.GridFields container item xs={12}>
          <S.GridInput item sm={12} md={4}>
            <Input
              fullWidth
              label={t('indCertifications:certificationNumber')}
              value={certificationData.number}
              error={Boolean(err('number'))}
              helperText={err('number')}
              onInput={value => handleInput(value, 'number')}
              disabled={!!certification.number}
            />
          </S.GridInput>
          <S.GridInput item sm={12} md={4}>
            <InputDate
              fullWidth
              label={t('indCertifications:emissionDate')}
              value={certificationData.dtEmitted}
              error={Boolean(err('dtEmitted'))}
              helperText={err('dtEmitted')}
              onInput={value => handleInput(value, 'dtEmitted')}
            />
          </S.GridInput>
          <S.GridInput item sm={12} md={4}>
            <InputDate
              fullWidth
              label={t('indCertifications:expirationDate')}
              value={certificationData.dtExpiry}
              error={Boolean(err('dtExpiry'))}
              helperText={err('dtExpiry')}
              onInput={value => handleInput(value, 'dtExpiry')}
            />
          </S.GridInput>
        </S.GridFields>

        <S.BoxTitle style={{ marginTop: 20 }}>
          {t('fileUpload:logo')}
        </S.BoxTitle>
        <S.GridFields container item sm={12}>
          <FileUpload
            name="logo"
            file={certificationData.logo || {}}
            onLoadImage={handleLoadImage}
            onRemoveImage={handleRemoveImage}
            acceptTypes="images"
            variant="square"
          />
        </S.GridFields>

        <S.GridHeader>
          <S.GridTitle item container xs={12}>
            <S.BoxTitle>
              {t('indCertifications:titleNotificationInfo')}
            </S.BoxTitle>
          </S.GridTitle>
        </S.GridHeader>

        <S.GridFields container item xs={12}>
          <S.GridInput item sm={12} md={4}>
            <InputSelect
              fullWidth
              error={Boolean(err('alert'))}
              helperText={err('alert')}
              label={t('indCertifications:timeAlert')}
              onSelected={({ value }) => handleInput(value, 'alert')}
              optionLabel="label"
              optionValue="value"
              options={alert()}
              value={certificationData.alert}
            />
          </S.GridInput>
          <S.GridFields container item xs={12}>
            <S.GridInput item sm={12} md={12}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'start',
                  flex: 1,
                  width: '100%'
                }}
              >
                <S.GridInput item sm={6} md={12}>
                  {certificationData.emails.split(',').map((email, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <Input
                        fullWidth
                        value={email}
                        onChange={event => handleNewEmailChange(event, index)}
                        label={t('indCertifications:notificationsEmails')}
                        onBlur={handleEmailBlur}
                        error={!!errors.emails}
                        helperText={errors.emails}
                      />
                      {index > 0 && (
                        <IconButton
                          style={{
                            backgroundColor: '#FAFAFA',
                            position: 'absolute',
                            top: 5,
                            right: 15
                          }}
                          aria-label="Delete file"
                          component="span"
                          disabled={
                            certificationData.emails.split(',').length > 5
                          }
                          onClick={handleRemoveEmail}
                        >
                          <Icon
                            icon="close"
                            width={15}
                            height={15}
                            fill="#A4A4A4"
                          />
                        </IconButton>
                      )}
                    </div>
                  ))}
                </S.GridInput>

                <IconButton
                  style={{ backgroundColor: '#00306D', marginTop: 10 }}
                  aria-label="Delete file"
                  component="span"
                  disabled={certificationData.emails.split(',').length >= 5}
                  onClick={handleAddEmail}
                >
                  <Icon
                    icon="dropdown-email-add"
                    width={20}
                    height={20}
                    fill="#ffffff"
                  />
                </IconButton>
              </div>
            </S.GridInput>
          </S.GridFields>
        </S.GridFields>
        <S.BoxTitle style={{ marginTop: 20 }}>
          {t('fileUpload:file')}
        </S.BoxTitle>
        <S.GridFields container item xs={12}>
          <FileUpload
            name="file"
            file={certificationData.file || {}}
            onLoadImage={handleLoadImage}
            onRemoveImage={handleRemoveImage}
            acceptTypes="pdf"
            variant="square"
            error={err('file')}
            helperText={err('file')}
          />
        </S.GridFields>
      </S.FullGrid>
    </S.Wrapper>
  )

  return (
    <Modal
      button={button}
      content={content}
      isActionsEnabled={true}
      width={100}
      onExit={onExit}
    />
  )
}
